import { Dispatch, SetStateAction, SyntheticEvent, useEffect, useState } from "react";

import { CategoryType } from "../../screens/SupportScreen";
import { FAQType } from "../../data_provider/gateways/FAQGateway";

import ScreenTitle from "../common/ScreenTitle";
import Search, { FAQSearchType, SearchFilters, SearchOptions } from "../common/Search";
import BoardListView from "../common/BoardListView";
import FAQItem from "./FAQItem";

import DataProvider from "../../data_provider/DataProvider";

import reqCategory from "../../dummy/process=type";
import reqFaqPopularList from "../../dummy/popularlist";

interface SupportMainProps {
    categories: CategoryType[];
    pageType?: number;
    // changePageMode: (asis: string, tobe: "main" | "list" | "view") => void;
    changePageMode: (
        asis: string,
        tobe: "main" | "list" | "view",
        opts?: { replace?: boolean; q?: string; type?: number; page?: number; no?: number }
    ) => void;
    changePageType: Dispatch<SetStateAction<number>>;
    // changePageNo: (no: number) => void;
    changePostNo: Dispatch<SetStateAction<number>>;
    setSearchOptions: Dispatch<SetStateAction<SearchOptions | undefined>>;
    setSearchedPageType: Dispatch<SetStateAction<FAQSearchType>>;
}

/**
 * @author khy, smyun
 * @date 2022-08-24 14:11, 2025-05-21
 * @remark 지원문의 화면_메인
 * @param SupportMainProps
 * categories:  키워드 분류(select box) 선택 시 부모 컴포넌트에게 변경되는 분류 전달
 * changePageMode (required): PageMode 변경 시 부모 컴포넌트에게 변경되는 모드 전달
 * changePageType (required): PageType 변경 시 부모 컴포넌트에게 변경되는 FAQ Type 전달
 * changePostNo (required): PostNo 변경 시 부모 컴포넌트에게 변경되는 페이지 번호 전달
 * setSearchOptions: 검색 옵션 세팅
 * setSearchedPageType: 검색된 키워드 분류 세팅
 * @returns SupportMain
 */

function SupportMain(props: SupportMainProps) {
    const [searchFilters, setSearchFilters] = useState<SearchFilters[]>([]); // <Search > 이용 타입 변경 시 ..
    const [popularLists, setPopularLists] = useState<Record<number, any[]>>({}); //

    useEffect(() => {
        if (props.categories.length > 0) {
            loadPopular();
            const categories = [{ no: "0", name: "전체" }, ...props.categories] as CategoryType[];
            const filters = categories.map(
                (cat: CategoryType) =>
                    ({
                        value: String(cat.no),
                        name: cat.name,
                    } as SearchFilters)
            );

            setSearchFilters(filters);
        }
    }, [props.categories]);

    const loadPopular = async () => {
        try {
            // 인기글 요청
            const results = await Promise.all(
                props.categories.map((cat: CategoryType) => {
                    return DataProvider.GetFAQPopularList({ type: String(cat.no) } as FAQType)
                        .then((res) => ({ [cat.no]: res.flag ? res.result : [] }))
                        .catch((err) => {
                            console.error("요청 실패:", cat.no, err);
                            return { [cat.no]: [] };
                        });
                })
            );

            const lists = results.reduce((acc, cur) => ({ ...acc, ...cur }), {});
            setPopularLists(lists);
        } catch (error) {
            console.error("FAQ 카테고리 또는 인기글 로딩 실패:", error);
        }
    };

    const handleSearch = (options: SearchOptions | undefined) => {
        props.setSearchOptions(options); // 검색 옵션 세팅
        props.setSearchedPageType(String(props.pageType) as FAQSearchType); // 검색 결과 키워드 제목
        // history에 추가할 pageMode 추가
        props.changePageMode("main", "list", { replace: false, type: props.pageType ?? 0, page: 1, q: options?.keyword ?? "" });
    };

    const handleChange = (value: string) => {
        props.changePageType(Number(value));
    };

    const getImage = (no: number) => {
        try {
            return require(`../../img/inquire_0${String(no)}.png`);
        } catch (error) {
            return require(`../../img/inquire_01.png`);
        }
    };

    const renderFaqCategory = (item: any) => {
        if (item) {
            const image = getImage(item.no);
            const list = popularLists[item.no] ?? [];

            const onImageClick = (categoryNo: number, event: SyntheticEvent) => {
                props.changePageType(categoryNo); // pageType 변경
                props.setSearchedPageType(String(categoryNo) as FAQSearchType);
                props.setSearchOptions({ keyword: "" }); // 검색이 동작하도록 옵션 설정

                props.changePageMode("main", "list", { replace: false, type: categoryNo, page: 1, q: "" });
            };

            const onListClick = (item: any, event: SyntheticEvent) => {
                // props.changePageNo(item.NO);
                props.changePostNo(item.NO); // 질문글 번호 변경
                // props.changePageMode("main", "view"); // 페이지 이동
                props.changePageMode("main", "view", { replace: false, no: item.NO });
            };

            return <FAQItem image={image} no={item.no} title={item.name} items={list} onImageClick={onImageClick} onListClick={onListClick} />;
        }
    };

    return (
        <>
            <ScreenTitle title="지원문의" />
            <Search label={"키워드 검색"} filters={searchFilters} doSearch={handleSearch} onFilterChange={handleChange} type="header" />
            {<BoardListView data={props.categories} renderItem={renderFaqCategory} itemsInRow={1} padding="0px" margin="30px 0px" />}
        </>
    );
}

export default SupportMain;
